import React from 'react';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/pageTranslations';

// Template para páginas de detalle de resultado
const ResultadoDetalle = ({ titulo, descripcion, imagenAntes, imagenDespues, contenido }) => {
  const navigate = useNavigate();
  const lang = useLanguage();

  const texts = {
    es: {
      backTo: 'Volver a Resultados',
      before: 'ANTES',
      after: 'DESPUÉS',
      aboutCase: 'Sobre este caso',
      treatmentFeatures: 'Características del tratamiento:',
      features: ['Procedimiento ambulatorio', 'Sin anestesia general', 'Resultados inmediatos', 'Recuperación inmediata'],
      disclaimer: 'Las fotografías muestran una persona real antes y después del tratamiento realizado por el Dr. Ángel Ruiz Norro. El paciente ha autorizado por escrito el que sean mostradas. Las fotos no han sido retocadas ni procesadas.',
      ctaTitle: '¿Quieres ver cómo quedaría tu nariz?',
      ctaDesc: 'Durante su consulta en Marbella, el Dr. Norro le mostrará casos similares al tuyo.',
      ctaButton: 'Solicitar Estudio Personalizado',
    },
    en: {
      backTo: 'Back to Results',
      before: 'BEFORE',
      after: 'AFTER',
      aboutCase: 'About this case',
      treatmentFeatures: 'Treatment features:',
      features: ['Outpatient procedure', 'No general anesthesia', 'Immediate results', 'Immediate recovery'],
      disclaimer: 'The photographs show a real person before and after the treatment performed by Dr. Ángel Ruiz Norro. The patient has given written authorization for them to be shown. The photos have not been retouched or processed.',
      ctaTitle: 'Want to see how your nose would look?',
      ctaDesc: 'During your consultation in Marbella, Dr. Norro will show you cases similar to yours.',
      ctaButton: 'Request Personalized Study',
    },
    fr: {
      backTo: 'Retour aux Résultats',
      before: 'AVANT',
      after: 'APRÈS',
      aboutCase: 'À propos de ce cas',
      treatmentFeatures: 'Caractéristiques du traitement:',
      features: ['Procédure ambulatoire', 'Sans anesthésie générale', 'Résultats immédiats', 'Récupération immédiate'],
      disclaimer: 'Les photographies montrent une personne réelle avant et après le traitement réalisé par le Dr. Ángel Ruiz Norro. Le patient a donné son autorisation écrite pour qu\'elles soient montrées. Les photos n\'ont pas été retouchées ni traitées.',
      ctaTitle: 'Vous voulez voir à quoi ressemblerait votre nez?',
      ctaDesc: 'Lors de votre consultation à Marbella, le Dr. Norro vous montrera des cas similaires au vôtre.',
      ctaButton: 'Demander une Étude Personnalisée',
    },
    pt: {
      backTo: 'Voltar para Resultados',
      before: 'ANTES',
      after: 'DEPOIS',
      aboutCase: 'Sobre este caso',
      treatmentFeatures: 'Características do tratamento:',
      features: ['Procedimento ambulatorial', 'Sem anestesia geral', 'Resultados imediatos', 'Recuperação imediata'],
      disclaimer: 'As fotografias mostram uma pessoa real antes e depois do tratamento realizado pelo Dr. Ángel Ruiz Norro. O paciente autorizou por escrito que fossem mostradas. As fotos não foram retocadas nem processadas.',
      ctaTitle: 'Quer ver como ficaria seu nariz?',
      ctaDesc: 'Durante sua consulta em Marbella, o Dr. Norro mostrará casos semelhantes ao seu.',
      ctaButton: 'Solicitar Estudo Personalizado',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion/resultados')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.backTo}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6">
            {titulo}
          </h1>
          <p className="text-xl text-text-secondary">
            {descripcion}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          
          {/* Images */}
          {imagenAntes && imagenDespues && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-ivory rounded-none shadow-lg overflow-hidden">
                <div className="p-4 bg-gray-100 text-center">
                  <span className="font-semibold text-[#1C1C1A]">{t.before}</span>
                </div>
                <img 
                  src={imagenAntes} 
                  alt={t.before}
                  className="w-full h-auto"
                />
              </div>
              <div className="bg-ivory rounded-none shadow-lg overflow-hidden">
                <div className="p-4 bg-gold/10 text-center">
                  <span className="font-semibold text-gold">{t.after}</span>
                </div>
                <img 
                  src={imagenDespues} 
                  alt={t.after}
                  className="w-full h-auto"
                />
              </div>
            </div>
          )}

          {/* Description */}
          <div className="bg-ivory p-8 rounded-none shadow-lg mb-8">
            <h2 className="text-2xl font-light text-[#1C1C1A] mb-6">{t.aboutCase}</h2>
            <div className="text-text-secondary leading-relaxed mb-6">
              {contenido}
            </div>
            
            <div className="bg-cream p-6 rounded-none">
              <h3 className="font-semibold text-[#1C1C1A] mb-4">{t.treatmentFeatures}</h3>
              <ul className="space-y-2">
                {t.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <CheckCircle className="text-gold" size={18} />
                    <span className="text-text-secondary">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Note */}
          <div className="bg-cream p-6 rounded-none mb-8 text-center">
            <p className="text-sm text-text-secondary italic">
              {t.disclaimer}
            </p>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-[#1C1C1A] to-[#1C1C1A] p-8 rounded-none text-white text-center">
            <h3 className="text-2xl font-light mb-4">{t.ctaTitle}</h3>
            <p className="text-white/80 mb-6">
              {t.ctaDesc}
            </p>
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              className="bg-gold hover:bg-gold text-white px-8 py-3"
            >
              {t.ctaButton}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

// Páginas individuales de resultados con imágenes reales
export const CorreccionRinoplastia = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Corrección de Rinoplastia Previa', descripcion: 'Solución de problemas en rinoplastias previas', contenido: 'Esta paciente había sido operada de rinoplastia en otro servicio. Presentaba un dorso irregular con prominencia en la parte baja y un ángulo nasolabial cerrado: aquí el ángulo entre la columela y el labio es de 90º grados o menos. El Dr. Ángel Ruiz Norro realizó una RINOMODELACIÓN regularizando el dorso y levantando la punta, consiguiendo un resultado natural sin necesidad de nueva cirugía.' },
    en: { titulo: 'Previous Rhinoplasty Correction', descripcion: 'Solution of problems in previous rhinoplasties', contenido: 'This patient had undergone rhinoplasty at another service. She presented an irregular dorsum with prominence in the lower part and a closed nasolabial angle: here the angle between the columella and the lip is 90 degrees or less. Dr. Ángel Ruiz Norro performed a RHINOMODELING regularizing the dorsum and lifting the tip, achieving a natural result without the need for new surgery.' },
    fr: { titulo: 'Correction de Rhinoplastie Précédente', descripcion: 'Solution de problèmes dans les rhinoplasties précédentes', contenido: 'Cette patiente avait été opérée d\'une rhinoplastie dans un autre service. Elle présentait un dos irrégulier avec une proéminence dans la partie basse et un angle nasolabial fermé: ici l\'angle entre la columelle et la lèvre est de 90 degrés ou moins. Le Dr. Ángel Ruiz Norro a réalisé un RHINOMODELAGE régularisant le dos et relevant la pointe, obtenant un résultat naturel sans nécessité d\'une nouvelle chirurgie.' },
    pt: { titulo: 'Correção de Rinoplastia Anterior', descripcion: 'Solução de problemas em rinoplastias anteriores', contenido: 'Esta paciente havia sido operada de rinoplastia em outro serviço. Apresentava um dorso irregular com proeminência na parte baixa e um ângulo nasolabial fechado: aqui o ângulo entre a columela e o lábio é de 90 graus ou menos. O Dr. Ángel Ruiz Norro realizou uma RINOMODELAÇÃO regularizando o dorso e levantando a ponta, conseguindo um resultado natural sem necessidade de nova cirurgia.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765135.jpg?t=1521738840"
      imagenDespues="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765137.jpg?t=1496397202"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export const Ejemplo2 = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Rinomodelación Caso 2', descripcion: 'Con anestesia local, sin quirófano ni hospitalización', contenido: 'El estudio de imagen computerizado nos ayudó a valorar diferentes posibilidades de cambio en esta paciente. Analizamos las opciones quirúrgicas y no quirúrgicas, beneficios, riesgos, costo, ventajas e inconvenientes. Optamos finalmente por realizar una RINOMODELACIÓN. Se corrigió el ángulo nasofrontal, radix, y se elevó la punta.' },
    en: { titulo: 'Rhinomodeling Case 2', descripcion: 'With local anesthesia, no operating room or hospitalization', contenido: 'The computerized image study helped us evaluate different possibilities of change in this patient. We analyzed surgical and non-surgical options, benefits, risks, cost, advantages and disadvantages. We finally opted to perform a RHINOMODELING. The nasofrontal angle, radix were corrected, and the tip was raised.' },
    fr: { titulo: 'Rhinomodelage Cas 2', descripcion: 'Avec anesthésie locale, sans bloc opératoire ni hospitalisation', contenido: 'L\'étude d\'image informatisée nous a aidé à évaluer différentes possibilités de changement chez cette patiente. Nous avons analysé les options chirurgicales et non chirurgicales, les avantages, les risques, le coût, les avantages et les inconvénients. Nous avons finalement opté pour réaliser un RHINOMODELAGE. L\'angle nasofrontal, le radix ont été corrigés et la pointe a été relevée.' },
    pt: { titulo: 'Rinomodelação Caso 2', descripcion: 'Com anestesia local, sem centro cirúrgico nem hospitalização', contenido: 'O estudo de imagem computadorizado nos ajudou a avaliar diferentes possibilidades de mudança nesta paciente. Analisamos as opções cirúrgicas e não cirúrgicas, benefícios, riscos, custo, vantagens e desvantagens. Optamos finalmente por realizar uma RINOMODELAÇÃO. O ângulo nasofrontal, radix foram corrigidos e a ponta foi elevada.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765138.jpg?t=1496397202"
      imagenDespues="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765139.jpg?t=1496397203"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export const Ejemplo3 = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Rinomodelación Caso 3', descripcion: 'Resultados naturales sin cirugía', contenido: 'Esta paciente deseaba cambiar su imagen y en particular la estética nasal pero no quería someterse a una intervención quirúrgica. Estudiamos las diferentes posibilidades sin cirugía. Encontramos algunas soluciones que podían satisfacer a la paciente y mejorar su imagen y su autoimagen sin pasar por el quirófano. La Rinomodelación que realizamos le ha proporcionado este nuevo aspecto natural.' },
    en: { titulo: 'Rhinomodeling Case 3', descripcion: 'Natural results without surgery', contenido: 'This patient wanted to change her image and particularly the nasal aesthetics but did not want to undergo a surgical intervention. We studied the different possibilities without surgery. We found some solutions that could satisfy the patient and improve her image and self-image without going through the operating room. The Rhinomodeling we performed has given her this new natural look.' },
    fr: { titulo: 'Rhinomodelage Cas 3', descripcion: 'Résultats naturels sans chirurgie', contenido: 'Cette patiente souhaitait changer son image et en particulier l\'esthétique nasale mais ne voulait pas subir une intervention chirurgicale. Nous avons étudié les différentes possibilités sans chirurgie. Nous avons trouvé des solutions qui pouvaient satisfaire la patiente et améliorer son image et son image de soi sans passer par le bloc opératoire. Le Rhinomodelage que nous avons réalisé lui a donné ce nouveau look naturel.' },
    pt: { titulo: 'Rinomodelação Caso 3', descripcion: 'Resultados naturais sem cirurgia', contenido: 'Esta paciente desejava mudar sua imagem e em particular a estética nasal, mas não queria se submeter a uma intervenção cirúrgica. Estudamos as diferentes possibilidades sem cirurgia. Encontramos algumas soluções que poderiam satisfazer a paciente e melhorar sua imagem e autoimagem sem passar pelo centro cirúrgico. A Rinomodelação que realizamos proporcionou a ela este novo aspecto natural.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765140.jpg?t=1496397203"
      imagenDespues="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765142.png?t=1496397204"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export const Ejemplo4 = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Rinomodelación Caso 4', descripcion: 'Resultado inmediato visible', contenido: 'Esta paciente está emocionada al ver su resultado. Acaba de realizar la primera sesión de RINOMODELACIÓN. La imagen de después fue tomada apenas media hora después de la de antes. También el médico y sus ayudantes disfrutamos de ver el resultado del trabajo realizado. Poder ofrecer y realizar este tratamiento es una satisfacción para nosotros.' },
    en: { titulo: 'Rhinomodeling Case 4', descripcion: 'Immediate visible result', contenido: 'This patient is excited to see her result. She has just completed the first RHINOMODELING session. The after image was taken just half an hour after the before. The doctor and his assistants also enjoy seeing the result of the work done. Being able to offer and perform this treatment is a satisfaction for us.' },
    fr: { titulo: 'Rhinomodelage Cas 4', descripcion: 'Résultat immédiat visible', contenido: 'Cette patiente est émue de voir son résultat. Elle vient de terminer la première séance de RHINOMODELAGE. L\'image après a été prise à peine une demi-heure après celle d\'avant. Le médecin et ses assistants apprécient également de voir le résultat du travail effectué. Pouvoir offrir et réaliser ce traitement est une satisfaction pour nous.' },
    pt: { titulo: 'Rinomodelação Caso 4', descripcion: 'Resultado imediato visível', contenido: 'Esta paciente está emocionada ao ver seu resultado. Ela acabou de realizar a primeira sessão de RINOMODELAÇÃO. A imagem de depois foi tirada apenas meia hora após a de antes. O médico e seus assistentes também apreciam ver o resultado do trabalho realizado. Poder oferecer e realizar este tratamento é uma satisfação para nós.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765144.jpg?t=1496397205"
      imagenDespues="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765143.jpg?t=1496397205"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export const Perfiloplastia = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Perfiloplastia Completa', descripcion: 'La estética nasal valorada en el conjunto facial', contenido: 'Este paciente presenta una acusada convexidad del perfil con dorso nasal prominente y mandíbula pequeña, que no afecta a la mordida. La corrección estética de la nariz y del mentón con la técnica de la RINOMODELACIÓN permite ofrecer este resultado sin quirófano ni hospitalización, con anestesia local. Se han modificado las proporciones de las medidas del rostro hacia una relación más armónica, realizando un aumento del mentón y rama mandibular con la técnica de bioplastia así como una corrección estética nasal.' },
    en: { titulo: 'Complete Profiloplasty', descripcion: 'Nasal aesthetics valued in the facial context', contenido: 'This patient presents a marked convexity of the profile with a prominent nasal dorsum and small jaw, which does not affect the bite. The aesthetic correction of the nose and chin with the RHINOMODELING technique allows offering this result without an operating room or hospitalization, with local anesthesia. The proportions of the facial measurements have been modified towards a more harmonious relationship, performing an augmentation of the chin and mandibular branch with the bioplasty technique as well as a nasal aesthetic correction.' },
    fr: { titulo: 'Profiloplastie Complète', descripcion: 'L\'esthétique nasale évaluée dans le contexte facial', contenido: 'Ce patient présente une convexité marquée du profil avec un dos nasal proéminent et une petite mâchoire, qui n\'affecte pas la morsure. La correction esthétique du nez et du menton avec la technique de RHINOMODELAGE permet d\'offrir ce résultat sans bloc opératoire ni hospitalisation, avec anesthésie locale. Les proportions des mesures faciales ont été modifiées vers une relation plus harmonieuse, réalisant une augmentation du menton et de la branche mandibulaire avec la technique de bioplastie ainsi qu\'une correction esthétique nasale.' },
    pt: { titulo: 'Perfiloplastia Completa', descripcion: 'A estética nasal avaliada no contexto facial', contenido: 'Este paciente apresenta uma acentuada convexidade do perfil com dorso nasal proeminente e mandíbula pequena, que não afeta a mordida. A correção estética do nariz e do queixo com a técnica de RINOMODELAÇÃO permite oferecer este resultado sem centro cirúrgico nem hospitalização, com anestesia local. As proporções das medidas faciais foram modificadas para uma relação mais harmônica, realizando um aumento do queixo e ramo mandibular com a técnica de bioplastia assim como uma correção estética nasal.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765145.jpg?t=1496397206"
      imagenDespues="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765146.jpg?t=1496397206"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export const Ejemplo6 = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Rinomodelación Caso 6', descripcion: 'Estética nasal sin cirugía', contenido: 'Este caso demuestra las posibilidades de la rinomodelación para conseguir cambios significativos en la estética nasal sin necesidad de cirugía. Sin palabras... el resultado habla por sí mismo. Dr. Ángel Ruiz Norro.' },
    en: { titulo: 'Rhinomodeling Case 6', descripcion: 'Nasal aesthetics without surgery', contenido: 'This case demonstrates the possibilities of rhinomodeling to achieve significant changes in nasal aesthetics without the need for surgery. Without words... the result speaks for itself. Dr. Ángel Ruiz Norro.' },
    fr: { titulo: 'Rhinomodelage Cas 6', descripcion: 'Esthétique nasale sans chirurgie', contenido: 'Ce cas démontre les possibilités du rhinomodelage pour obtenir des changements significatifs dans l\'esthétique nasale sans nécessité de chirurgie. Sans mots... le résultat parle de lui-même. Dr. Ángel Ruiz Norro.' },
    pt: { titulo: 'Rinomodelação Caso 6', descripcion: 'Estética nasal sem cirurgia', contenido: 'Este caso demonstra as possibilidades da rinomodelação para conseguir mudanças significativas na estética nasal sem necessidade de cirurgia. Sem palavras... o resultado fala por si mesmo. Dr. Ángel Ruiz Norro.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765147.jpg?t=1496397206"
      imagenDespues="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765148.jpg?t=1496397207"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export const Ejemplo7 = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Rinomodelación Caso 7', descripcion: 'Punta nasal levantada y nariz más pequeña', contenido: 'Esta paciente deseaba apenas un aspecto más levantado de la punta nasal y la apariencia de una nariz más pequeña. Con la RINOMODELACIÓN conseguimos realzar su belleza y realizar su deseo sin cirugía. Un resultado sutil pero significativo que mejora la armonía del rostro.' },
    en: { titulo: 'Rhinomodeling Case 7', descripcion: 'Raised nasal tip and smaller nose', contenido: 'This patient wanted just a more raised look of the nasal tip and the appearance of a smaller nose. With RHINOMODELING we managed to enhance her beauty and fulfill her wish without surgery. A subtle but significant result that improves facial harmony.' },
    fr: { titulo: 'Rhinomodelage Cas 7', descripcion: 'Pointe nasale relevée et nez plus petit', contenido: 'Cette patiente souhaitait juste un aspect plus relevé de la pointe nasale et l\'apparence d\'un nez plus petit. Avec le RHINOMODELAGE nous avons réussi à rehausser sa beauté et réaliser son souhait sans chirurgie. Un résultat subtil mais significatif qui améliore l\'harmonie du visage.' },
    pt: { titulo: 'Rinomodelação Caso 7', descripcion: 'Ponta nasal levantada e nariz menor', contenido: 'Esta paciente desejava apenas um aspecto mais levantado da ponta nasal e a aparência de um nariz menor. Com a RINOMODELAÇÃO conseguimos realçar sua beleza e realizar seu desejo sem cirurgia. Um resultado sutil mas significativo que melhora a harmonia do rosto.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765149.jpg?t=1496397207"
      imagenDespues="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765150.jpg?t=1496397208"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export const Ejemplo8 = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Rinomodelación Caso 8', descripcion: 'Corrección de dorso y definición de punta', contenido: 'Este paciente solicitó corregir el dorso y mejorar la definición de la punta. Tras un análisis de imagen en el ordenador realizamos una prueba con un resultado temporal para comprobar el resultado antes de realizar una corrección permanente. Esto permite al paciente ver cómo quedará antes de decidir el tratamiento definitivo.' },
    en: { titulo: 'Rhinomodeling Case 8', descripcion: 'Dorsum correction and tip definition', contenido: 'This patient requested to correct the dorsum and improve the definition of the tip. After a computer image analysis we performed a test with a temporary result to verify the result before performing a permanent correction. This allows the patient to see how it will look before deciding on the definitive treatment.' },
    fr: { titulo: 'Rhinomodelage Cas 8', descripcion: 'Correction du dos et définition de la pointe', contenido: 'Ce patient a demandé de corriger le dos et d\'améliorer la définition de la pointe. Après une analyse d\'image informatique, nous avons effectué un test avec un résultat temporaire pour vérifier le résultat avant d\'effectuer une correction permanente. Cela permet au patient de voir à quoi cela ressemblera avant de décider du traitement définitif.' },
    pt: { titulo: 'Rinomodelação Caso 8', descripcion: 'Correção do dorso e definição da ponta', contenido: 'Este paciente solicitou corrigir o dorso e melhorar a definição da ponta. Após uma análise de imagem no computador realizamos um teste com um resultado temporário para verificar o resultado antes de realizar uma correção permanente. Isso permite ao paciente ver como ficará antes de decidir o tratamento definitivo.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765153.jpg?t=1496397208"
      imagenDespues="https://www.rinomodelacion.com/s/cc_images/teaserbox_29765154.jpg?t=1496397209"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export const Ejemplo9 = () => {
  const lang = useLanguage();
  const texts = {
    es: { titulo: 'Rinomodelación Caso 9', descripcion: 'Perfil armonizado sin cirugía', contenido: 'Esta paciente deseaba mejorar la armonía de su perfil nasal. Mediante la técnica de rinomodelación conseguimos un resultado natural que realza su belleza sin necesidad de cirugía. El procedimiento se realizó con anestesia local, sin quirófano ni hospitalización, con un resultado inmediato y duradero.' },
    en: { titulo: 'Rhinomodeling Case 9', descripcion: 'Harmonized profile without surgery', contenido: 'This patient wanted to improve the harmony of her nasal profile. Through the rhinomodeling technique we achieved a natural result that enhances her beauty without the need for surgery. The procedure was performed with local anesthesia, without an operating room or hospitalization, with an immediate and lasting result.' },
    fr: { titulo: 'Rhinomodelage Cas 9', descripcion: 'Profil harmonisé sans chirurgie', contenido: 'Cette patiente souhaitait améliorer l\'harmonie de son profil nasal. Grâce à la technique de rhinomodelage, nous avons obtenu un résultat naturel qui met en valeur sa beauté sans nécessité de chirurgie. La procédure a été réalisée sous anesthésie locale, sans bloc opératoire ni hospitalisation, avec un résultat immédiat et durable.' },
    pt: { titulo: 'Rinomodelação Caso 9', descripcion: 'Perfil harmonizado sem cirurgia', contenido: 'Esta paciente desejava melhorar a harmonia do seu perfil nasal. Através da técnica de rinomodelação conseguimos um resultado natural que realça sua beleza sem necessidade de cirurgia. O procedimento foi realizado com anestesia local, sem centro cirúrgico nem hospitalização, com um resultado imediato e duradouro.' },
  };
  const t = texts[lang] || texts.es;
  return (
    <ResultadoDetalle 
      titulo={t.titulo}
      descripcion={t.descripcion}
      imagenAntes="/images/rinomodelacion-caso-nuevo-pre.jpg"
      imagenDespues="/images/rinomodelacion-caso-nuevo-post.jpg"
      contenido={<p>{t.contenido}</p>}
    />
  );
};

export default ResultadoDetalle;
